import { useCallback } from "react";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { formatDateTime } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

export interface MedicationDisplayFields {
  dosage: string;
  statusLabel: string;
  date: string;
}

/**
 * Formats an `enduser_medications` record into the display strings shared by the medications list
 * rows and the single-medication detail view, so both render a medication identically.
 */
export const useMedicationDisplay = () => {
  const { t } = useTranslation("contacts");

  return useCallback(
    (medication: TellescopeModelFromName<"enduser_medications">): MedicationDisplayFields => {
      const orderStatusLabels: Record<string, string> = {
        pending: t("Pending Review"),
        active: t("Active"),
        completed: t("Completed"),
        cancelled: t("Cancelled"),
      };

      const takenAt = medication.startedTakingAt || medication.createdAt;

      return {
        dosage:
          medication.dosage?.value && medication.dosage?.unit
            ? `${medication.dosage.value} ${medication.dosage.unit}`
            : medication.dosage?.description || t("Dosage not specified"),
        statusLabel: (medication.orderStatus && orderStatusLabels[medication.orderStatus]) || t("Unknown Status"),
        date: takenAt ? formatDateTime(takenAt, "MM/DD/YYYY") : t("Date not specified"),
      };
    },
    [t]
  );
};
